import { toast } from 'react-toastify';
import { ApiError } from '../../api';

export interface ErrorHandlingOptions {
  /** Custom error message prefix. If not provided, will use a generic message */
  messagePrefix?: string;
  /** Custom error message to display regardless of error content */
  messageOverride?: string | React.ReactElement;
  /** Whether to show a toast notification. Default: true */
  showToast?: boolean;
  /** Whether to handle 403 Forbidden errors. Default: false */
  handle403?: boolean;
  /** Whether to log to the console. Default: true */
  logToConsole?: boolean;
  /** Custom toast options */
  toastOptions?: {
    type?: 'error' | 'warning' | 'info';
    autoClose?: number | false;
  };
  /** Callback function to execute after handling the error */
  onError?: (error: unknown) => void;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const handleApiError = (error: unknown, options: ErrorHandlingOptions = {}) => {
  const {
    messagePrefix = null,
    messageOverride = null,
    showToast = true,
    handle403 = false,
    logToConsole = true,
    toastOptions = { type: 'error' },
    onError,
  } = options;

  // Skip handling auth errors - let global handler manage these
  if (error instanceof ApiError && (error.status === 401 || (error.status === 403 && !handle403))) {
    return;
  }
  let errorMessages: string[];
  if (error instanceof ApiError && error.status === 400) {
    errorMessages = extractValidationErrorMessages(error.body);
  } else {
    const errorMessage: string = getErrorMessage(error);
    const fullMessage: string = messagePrefix
      ? `${messagePrefix}: ${errorMessage}`
      : errorMessage || 'An error occurred';
    errorMessages = [fullMessage];
  }

  let toastContent: string | React.ReactElement;

  if (messageOverride) {
    toastContent = messageOverride;
  } else if (errorMessages.length > 1) {
    toastContent = (
      <div>
        <div className="tw:font-medium tw:mb-1">Please fix the following errors:</div>
        <ul className="tw:list-disc tw:list-inside">
          {errorMessages.map((message, index) => (
            <li key={index}>{message}</li>
          ))}
        </ul>
      </div>
    );
  } else if (errorMessages.length === 1) {
    toastContent = errorMessages[0];
  } else {
    toastContent = 'An error occurred';
  }

  // Show toast notification
  if (showToast) {
    const toastFn =
      toastOptions.type === 'warning'
        ? toast.warn
        : toastOptions.type === 'info'
          ? toast.info
          : toast.error;

    toastFn(toastContent, {
      autoClose: toastOptions.autoClose,
    });
  }

  // Log to console
  if (logToConsole) {
    console.error(messagePrefix || 'Error', error);
  }

  // Execute custom callback
  if (onError) {
    onError(error);
  }
};

const formatFieldName = (fieldName: string): string => {
  return fieldName
    .replace(/_/g, ' ') // Remove underscores
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Title case each word
};

// Helper function to extract validation error messages from API response
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractValidationErrorMessages = (errorData: any): string[] => {
  const errorMessages: string[] = [];

  if (Array.isArray(errorData)) {
    return [errorData.join(' ')];
  }

  // Add non_field_errors without field name prefix
  if (errorData.non_field_errors) {
    if (Array.isArray(errorData.non_field_errors)) {
      errorMessages.push(errorData.non_field_errors.join(' '));
    } else {
      errorMessages.push(errorData.non_field_errors);
    }
  }

  // Add detail without field name prefix
  if (errorData.detail) {
    errorMessages.push(errorData.detail);
  }

  // Add field-specific errors with formatted field names
  Object.entries(errorData).forEach(([field, messages]) => {
    if (field !== 'non_field_errors' && field !== 'detail') {
      const formattedFieldName = formatFieldName(field);
      if (Array.isArray(messages)) {
        errorMessages.push(`${formattedFieldName}: ${messages.join(' ')}`);
      } else {
        errorMessages.push(`${formattedFieldName}: ${messages}`);
      }
    }
  });

  return errorMessages;
};

// Helper function to extract meaningful error messages
const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    return error.body?.detail || error.message || 'Unknown API error';
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Unknown error';
};

// Alternative: Get all validation errors (useful for forms)
export const getValidationErrors = (error: unknown): Record<string, string[]> => {
  if (error instanceof ApiError && error.status === 400) {
    const validationErrors = error.body;

    if (typeof validationErrors === 'object' && validationErrors !== null) {
      const result: Record<string, string[]> = {};

      Object.entries(validationErrors).forEach(([field, errors]) => {
        if (Array.isArray(errors)) {
          result[field] = errors.filter((err): err is string => typeof err === 'string');
        } else if (typeof errors === 'string') {
          result[field] = [errors];
        } else {
          result[field] = [String(errors)];
        }
      });

      return result;
    }
  }

  return {};
};

// Helper for form field validation display
export const getFieldError = (error: unknown, fieldName: string): string | null => {
  const validationErrors = getValidationErrors(error);
  return validationErrors[fieldName]?.[0] || null;
};
